/**
 * Production guard for the FastAPI documentation endpoints (Requirements 12.1-12.3).
 *
 * The backend disables `/docs`, `/redoc`, and `/openapi.json` in production, so any
 * request that reaches them there is a mistake — usually a codegen helper or a
 * pasted URL — and it should fail loudly at the client rather than produce a
 * confusing 404 or, worse, be silently ignored.
 *
 * Everything here is pure apart from a single dev-console note: `isProductionEnv`
 * takes the environment as an argument (defaulting to `process.env`) so the guard is
 * testable without mutating globals, and `isDocumentationEndpoint` works on the URL
 * string alone.
 *
 * Codegen guidance for developers lives in `ENVIRONMENT_CONFIG.md` (Req 12.2).
 */

import { ERROR_KIND } from "./apiErrors";

/** Documentation paths disabled in production (Req 12.1). */
export const DOCUMENTATION_PATHS = ["/docs", "/redoc", "/openapi.json"];

/** Marker carried by the thrown error so `normalizeApiError` classifies it. */
export const DOCS_BLOCKED = ERROR_KIND.DOCS_BLOCKED;

/** Env values treated as production; `production` is what CRA sets for a build. */
const PRODUCTION_VALUES = new Set(["production", "prod"]);

/** Final path segment of each documentation path, matched case-insensitively. */
const DOCUMENTATION_SEGMENTS = new Set(
  DOCUMENTATION_PATHS.map((path) => path.replace(/^\/+/, "").toLowerCase())
);

/** Paths already noted in the console, so a dev run gets one note per endpoint. */
const notedPaths = new Set();

/** Error thrown when a documentation endpoint is requested in production. */
export class DocsEndpointBlockedError extends Error {
  constructor(url) {
    super(
      "API documentation endpoints are disabled in production. " +
        "Point codegen or schema tooling at a non-production instance instead."
    );
    this.name = "DocsEndpointBlockedError";
    // Both keys are read by apiErrors.classifyErrorKind; set both so the
    // descriptor is correct regardless of which one a future consumer checks.
    this.code = DOCS_BLOCKED;
    this.kind = DOCS_BLOCKED;
    this.url = typeof url === "string" ? url : "";
  }
}

/** Trimmed lowercase string, or null when there is nothing meaningful to read. */
function readEnvValue(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * True when the app is running against production.
 *
 * `REACT_APP_ENV` wins whenever it carries a value, so a UAT build produced with
 * `NODE_ENV=production` (which CRA always sets) is *not* treated as production and
 * keeps its access to the documentation endpoints. `NODE_ENV` is consulted only as
 * the fallback (Req 12.3).
 *
 * @param {Record<string, string|undefined>} [env] defaults to `process.env`
 * @returns {boolean}
 */
export function isProductionEnv(env = process.env) {
  const source = env == null || typeof env !== "object" ? {} : env;

  const explicit = readEnvValue(source.REACT_APP_ENV);
  if (explicit !== null) return PRODUCTION_VALUES.has(explicit);

  const nodeEnv = readEnvValue(source.NODE_ENV);
  return nodeEnv !== null && PRODUCTION_VALUES.has(nodeEnv);
}

/**
 * Reduces a URL to its request path.
 *
 * Handles the two shapes the shared Axios client sees: absolute URLs
 * (`https://host/api/docs?x=1`) and relative paths (`docs`, `/api/docs/`). The
 * query string and fragment are dropped before matching, since neither is part of
 * the path.
 */
function requestPath(url) {
  if (typeof url !== "string") return "";

  let raw = url.trim();
  if (raw === "") return "";

  const hashIndex = raw.indexOf("#");
  if (hashIndex !== -1) raw = raw.slice(0, hashIndex);

  const queryIndex = raw.indexOf("?");
  if (queryIndex !== -1) raw = raw.slice(0, queryIndex);

  const scheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.exec(raw);
  if (scheme) {
    const afterAuthority = raw.slice(scheme[0].length);
    const slash = afterAuthority.indexOf("/");
    return slash === -1 ? "" : afterAuthority.slice(slash);
  }

  if (raw.startsWith("//")) {
    // Protocol-relative URL: everything up to the first slash is the authority.
    const afterSlashes = raw.slice(2);
    const slash = afterSlashes.indexOf("/");
    return slash === -1 ? "" : afterSlashes.slice(slash);
  }

  return raw;
}

/** Percent-decodes a path segment, comparing raw text when the escape is broken. */
function decodeSegment(segment) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** Last non-empty path segment, so trailing slashes and `//` are tolerated. */
function lastSegment(path) {
  const parts = path.split("/");
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    if (parts[index] !== "") return decodeSegment(parts[index]);
  }
  return "";
}

/**
 * True when the URL targets one of the documentation endpoints.
 *
 * Matching is on the path's final segment, which gives the two behaviours the
 * requirement asks for: `https://host/api/docs?x=1`, `/api/docs/`, and a bare
 * `docs` all match, while lookalikes such as `/docsearch`, `/documentation`, and
 * `/api/docs/extra` do not, because a segment must match in full.
 *
 * @param {string} url absolute URL or relative path
 * @returns {boolean}
 */
export function isDocumentationEndpoint(url) {
  const segment = lastSegment(requestPath(url));
  if (segment === "") return false;
  return DOCUMENTATION_SEGMENTS.has(segment.toLowerCase());
}

/**
 * Blocks documentation requests in production and lets everything else through.
 *
 * In production a documentation URL throws `DocsEndpointBlockedError`, which
 * carries the `DOCS_BLOCKED` marker so `normalizeApiError` maps it onto
 * `ERROR_KIND.DOCS_BLOCKED`. Outside production the call passes through with one
 * console note per endpoint — enough to tell a developer the request only works
 * locally, without flooding the console on repeat calls.
 *
 * @param {string} url absolute URL or relative path
 * @param {Record<string, string|undefined>} [env] defaults to `process.env`
 * @returns {boolean} true when the request may proceed
 * @throws {DocsEndpointBlockedError} in production, for documentation endpoints only
 */
export function assertDocsRequestAllowed(url, env = process.env) {
  if (!isDocumentationEndpoint(url)) return true;

  if (isProductionEnv(env)) {
    throw new DocsEndpointBlockedError(url);
  }

  const key = requestPath(url).toLowerCase();
  if (!notedPaths.has(key)) {
    notedPaths.add(key);
    // eslint-disable-next-line no-console
    console.info(
      "[docsEndpointGuard] Requesting an API documentation endpoint " +
        `(${key || url}). This is disabled in production — keep codegen pointed at ` +
        "a non-production instance."
    );
  }

  return true;
}

/** Clears the console-note memo. Exists for tests; not used by application code. */
export function resetDocsNoticeLog() {
  notedPaths.clear();
}
